import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

const MAX_FILES = 10;

const formatSize = (bytes) =>
  bytes > 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/*
 * Drag-and-drop wrapper over the same multi-file input the page always had.
 * The files go up untouched as `additionalImages`; multer caps the upload at
 * ten, so the picker refuses the eleventh here rather than failing on POST.
 */
const PhotoDropzone = ({ files, onChange }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [previews, setPreviews] = useState([]);

  /* Object URLs are revoked when the selection changes so nothing leaks. */
  useEffect(() => {
    const urls = files.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const addFiles = (incoming) => {
    const images = Array.from(incoming).filter((file) =>
      file.type.startsWith("image/")
    );
    onChange([...files, ...images].slice(0, MAX_FILES));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeAt = (index) => onChange(files.filter((_, i) => i !== index));

  return (
    <div className="c4rtn__upload">
      <div
        className={`c4rtn__drop${dragging ? " c4rtn__drop--over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 16V5m0 0L8 9m4-4 4 4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <p className="c4rtn__drop-title">Drag photos here</p>
        <p className="c4rtn__drop-text">
          Clear photos of the item and its packaging help us settle the claim
          faster. Up to {MAX_FILES} images.
        </p>

        <button
          className="c4rtn__btn c4rtn__btn--outline"
          type="button"
          onClick={() => inputRef.current?.click()}
        >
          Browse Files
        </button>

        <input
          ref={inputRef}
          className="c4rtn__file"
          id="c4rtn-photos"
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {files.length ? (
        <ul className="c4rtn__previews">
          {files.map((file, index) => (
            <li className="c4rtn__preview" key={`${file.name}-${file.size}-${index}`}>
              {previews[index] ? (
                <img src={previews[index]} alt="" />
              ) : (
                <span className="c4rtn__preview-blank" aria-hidden="true" />
              )}
              <span className="c4rtn__preview-meta">
                <span className="c4rtn__preview-name">{file.name}</span>
                <span className="c4rtn__preview-size">{formatSize(file.size)}</span>
              </span>
              <button
                className="c4rtn__preview-remove"
                type="button"
                onClick={() => removeAt(index)}
              >
                <span className="c4rtn__sr-only">Remove {file.name}</span>
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

PhotoDropzone.propTypes = {
  files: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PhotoDropzone;
